/**
 * Refacturation page — dedicated screen for monthly inter-company recap.
 *
 * Shows:
 *  - Month selector
 *  - List of (from_company → to_company) pairs detected on that month
 *  - For the selected pair: full detail (synthesis by product + chronological lines)
 *  - "Télécharger PDF" button to generate the document destined to accounting
 *
 * Backend: /api/transfer-recap/pairs · /api/transfer-recap/detail · /api/transfer-recap/pdf
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Download, FileText, Loader2, Receipt } from 'lucide-react';
import PrintButton from '@/components/PrintButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { transfersAPI, downloadAuthFile } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function RefacturationPage() {
    const defaultPeriod = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();
    const [period, setPeriod] = useState(defaultPeriod);

    // Available months: from Jan 2022 to current month, most recent first
    const monthOptions = useMemo(() => {
        const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        const out = [];
        const now = new Date();
        const startYear = 2022;
        for (let y = now.getFullYear(); y >= startYear; y--) {
            const lastMonth = (y === now.getFullYear()) ? now.getMonth() + 1 : 12;
            for (let m = lastMonth; m >= 1; m--) {
                const key = `${y}-${String(m).padStart(2, '0')}`;
                out.push({ key, label: `${MONTHS_FR[m - 1]} ${y}` });
            }
        }
        return out;
    }, []);
    const [pairs, setPairs] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loadingPairs, setLoadingPairs] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Reload pairs when the period changes
    useEffect(() => {
        (async () => {
            setLoadingPairs(true);
            setSelectedKey(null);
            setDetail(null);
            try {
                const r = await transfersAPI.getMonthlyPairs(period);
                setPairs(r.data?.pairs || []);
            } catch (e) { setPairs([]); }
            finally { setLoadingPairs(false); }
        })();
    }, [period]);

    // Auto-select first pair when pairs change; if current key is still valid, keep it.
    useEffect(() => {
        if (pairs.length === 0) {
            if (selectedKey !== null) setSelectedKey(null);
            return;
        }
        const validKeys = new Set(pairs.map(p => `${p.from_company}|${p.to_company}`));
        if (!selectedKey || !validKeys.has(selectedKey)) {
            setSelectedKey(`${pairs[0].from_company}|${pairs[0].to_company}`);
        }
    }, [pairs, selectedKey]);

    // Load detail when a pair is selected
    useEffect(() => {
        if (!selectedKey) { setDetail(null); return; }
        const [from, to] = selectedKey.split('|');
        (async () => {
            setLoadingDetail(true);
            try {
                const r = await transfersAPI.getMonthlyRecapDetail(period, from, to);
                setDetail(r.data);
            } catch (e) { setDetail(null); }
            finally { setLoadingDetail(false); }
        })();
    }, [selectedKey, period]);

    const selectedPair = useMemo(() => {
        if (!selectedKey) return null;
        const [from, to] = selectedKey.split('|');
        return { from, to };
    }, [selectedKey]);

    const handleDownloadPdf = () => {
        if (!selectedPair) return;
        downloadAuthFile(
            transfersAPI.getMonthlyRecapPdfUrl(period, selectedPair.from, selectedPair.to),
            `refacturation-${period}-${selectedPair.from}-vers-${selectedPair.to}.pdf`
        );
    };

    return (
        <div className="space-y-4 animate-in fade-in duration-300" data-testid="refacturation-page">
            {/* Header */}
            <Card>
                <CardContent className="p-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                            <Receipt className="w-4 h-4 text-amber-600" />
                            Refacturation inter-entreprises
                            <span className="text-[11px] font-normal text-slate-400 ml-1 hidden md:inline">
                                — Vérifiez les transferts avant de générer le PDF
                            </span>
                        </h2>
                        <div className="flex items-center gap-1.5">
                            <Label htmlFor="refac-period" className="text-[11px] text-slate-500 whitespace-nowrap">Mois :</Label>
                            <Select value={period} onValueChange={setPeriod}>
                                <SelectTrigger id="refac-period" className="h-7 w-40 text-xs" data-testid="refac-period-input">
                                    <SelectValue placeholder="Choisir un mois" />
                                </SelectTrigger>
                                <SelectContent className="max-h-72">
                                    {monthOptions.map(opt => (
                                        <SelectItem
                                            key={opt.key}
                                            value={opt.key}
                                            data-testid={`refac-period-option-${opt.key}`}
                                        >
                                            {opt.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Pair selector */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        Paires inter-entreprises détectées sur {period}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                    {loadingPairs ? (
                        <div className="text-xs text-slate-400 italic flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Chargement...
                        </div>
                    ) : pairs.length === 0 ? (
                        <div className="text-xs text-slate-400 italic">Aucun transfert inter-entreprises sur ce mois.</div>
                    ) : (
                        <>
                            {/* Dropdown — easier scan when many pairs */}
                            <Select
                                value={selectedKey || ''}
                                onValueChange={(v) => setSelectedKey(v)}
                            >
                                <SelectTrigger className="w-full sm:max-w-md" data-testid="refac-pair-select">
                                    <SelectValue placeholder="Choisir une paire à refacturer" />
                                </SelectTrigger>
                                <SelectContent>
                                    {pairs.map((p) => {
                                        const key = `${p.from_company}|${p.to_company}`;
                                        return (
                                            <SelectItem
                                                key={key}
                                                value={key}
                                                data-testid={`refac-pair-option-${p.from_company}-${p.to_company}`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <span className="font-semibold">{p.from_company}</span>
                                                    <ArrowRight className="w-3 h-3 text-slate-400" />
                                                    <span className="font-semibold">{p.to_company}</span>
                                                    <span className="text-slate-400 text-[10px] ml-1">({p.n_transfers} transfert{p.n_transfers > 1 ? 's' : ''}, {p.n_lines} ligne{p.n_lines > 1 ? 's' : ''})</span>
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>

                            {/* Quick-pick chips for power users (small screens get only the select) */}
                            <div className="hidden md:flex flex-wrap gap-1.5">
                                {pairs.map((p) => {
                                    const key = `${p.from_company}|${p.to_company}`;
                                    const active = selectedKey === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setSelectedKey(key)}
                                            className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] border transition-colors ${
                                                active
                                                    ? 'bg-slate-900 text-white border-slate-900'
                                                    : 'bg-white text-slate-600 border-slate-200 hover:border-amber-400'
                                            }`}
                                            data-testid={`refac-pair-${p.from_company}-${p.to_company}`}
                                        >
                                            <span>{p.from_company}</span>
                                            <ArrowRight className="w-2.5 h-2.5" />
                                            <span>{p.to_company}</span>
                                            <Badge variant="secondary" className={`text-[9px] ml-0.5 px-1 py-0 ${active ? 'bg-slate-700 text-slate-100' : ''}`}>
                                                {p.n_transfers}
                                            </Badge>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Detail */}
            {selectedPair && (
                <DetailView
                    detail={detail}
                    loading={loadingDetail}
                    onDownload={handleDownloadPdf}
                    printUrl={() => transfersAPI.getMonthlyRecapPdfUrl(period, selectedPair.from, selectedPair.to)}
                    fromCompany={selectedPair.from}
                    toCompany={selectedPair.to}
                />
            )}
        </div>
    );
}

function DetailView({ detail, loading, onDownload, printUrl, fromCompany, toCompany }) {
    if (loading) {
        return (
            <Card>
                <CardContent className="py-10 text-center text-slate-400">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                </CardContent>
            </Card>
        );
    }
    if (!detail) return null;
    return (
        <>
            {/* KPIs + download */}
            <Card className="border-amber-200 bg-amber-50/30">
                <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-900">{fromCompany}</span>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <span className="font-bold text-slate-900">{toCompany}</span>
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-4 flex-wrap">
                            <span><strong className="text-slate-700">{detail.n_transfers}</strong> transferts</span>
                            <span><strong className="text-slate-700">{detail.n_lines}</strong> lignes</span>
                            <span data-testid="refac-total-ht">
                                Total HT : <strong className="text-slate-700">{formatCurrency(detail.grand_total)}</strong>
                            </span>
                            <span data-testid="refac-total-tva">
                                TVA : <strong className="text-slate-700">{formatCurrency(detail.total_tva || 0)}</strong>
                            </span>
                            <span data-testid="refac-total-ttc">
                                Total TTC : <strong className="text-amber-700">{formatCurrency(detail.total_ttc ?? detail.grand_total)}</strong>
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={onDownload}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid="refac-download-pdf"
                        >
                            <Download className="w-4 h-4 mr-1.5" />
                            Télécharger le PDF
                        </Button>
                        <PrintButton size="default" testId="refac-print-pdf" url={printUrl} />
                    </div>
                </CardContent>
            </Card>

            {/* Section 1: synthesis by product */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />
                        1. Synthèse par produit ({detail.summary.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    {detail.summary.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs text-slate-400">Aucune ligne</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-1.5">Produit</th>
                                    <th className="text-left px-3 py-1.5">Marque</th>
                                    <th className="text-right px-3 py-1.5">Qté</th>
                                    <th className="text-left px-3 py-1.5">Unité</th>
                                    <th className="text-right px-3 py-1.5">Nb transferts</th>
                                    <th className="text-right px-3 py-1.5">Total HT</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.summary.map((s, i) => (
                                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 1 ? 'bg-slate-100' : 'bg-white'}`}>
                                        <td className="px-3 py-1 truncate max-w-[320px]" title={s.product_name}>{s.product_name}</td>
                                        <td className="px-3 py-1 text-slate-500">{s.brand || '-'}</td>
                                        <td className="px-3 py-1 text-right tabular-nums">{s.qty}</td>
                                        <td className="px-3 py-1 text-slate-500">{s.unit}</td>
                                        <td className="px-3 py-1 text-right tabular-nums text-slate-500">{s.n_transfers}</td>
                                        <td className="px-3 py-1 text-right font-semibold tabular-nums">{formatCurrency(s.total)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-amber-50 border-t-2 border-amber-200">
                                    <td colSpan={5} className="px-3 py-1.5 text-right text-xs font-bold text-slate-700">TOTAL HT :</td>
                                    <td className="px-3 py-1.5 text-right text-xs font-bold text-slate-800 tabular-nums" data-testid="refac-table-total-ht">{formatCurrency(detail.grand_total)}</td>
                                </tr>
                                {(detail.vat_breakdown || []).map((v) => (
                                    <tr key={v.rate} className="bg-amber-50">
                                        <td colSpan={5} className="px-3 py-1 text-right text-xs font-semibold text-slate-600">
                                            TVA {String(v.rate).replace('.', ',')}%{(detail.vat_breakdown || []).length > 1 ? ` (base ${formatCurrency(v.base)})` : ''} :
                                        </td>
                                        <td className="px-3 py-1 text-right text-xs font-semibold text-slate-700 tabular-nums">{formatCurrency(v.tva)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-amber-100 border-t border-amber-300">
                                    <td colSpan={5} className="px-3 py-1.5 text-right text-xs font-bold text-slate-800">TOTAL TTC :</td>
                                    <td className="px-3 py-1.5 text-right text-sm font-bold text-amber-800 tabular-nums" data-testid="refac-table-total-ttc">{formatCurrency(detail.total_ttc ?? detail.grand_total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Section 2: chronological detail */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-slate-500" />
                        2. Détail chronologique ({detail.detail.length} lignes)
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    {detail.detail.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs text-slate-400">Aucune ligne</div>
                    ) : (
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-1.5">Date</th>
                                    <th className="text-left px-3 py-1.5">N° TR</th>
                                    <th className="text-left px-3 py-1.5">Site origine</th>
                                    <th className="text-left px-3 py-1.5">Site destination</th>
                                    <th className="text-left px-3 py-1.5">Produit</th>
                                    <th className="text-right px-3 py-1.5">Qté</th>
                                    <th className="text-left px-3 py-1.5">Unité</th>
                                    <th className="text-right px-3 py-1.5">P.U.</th>
                                    <th className="text-right px-3 py-1.5">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.detail.map((r, i) => (
                                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 1 ? 'bg-slate-100' : 'bg-white'}`}>
                                        <td className="px-3 py-1 text-slate-600">{r.date}</td>
                                        <td className="px-3 py-1 font-mono text-[10px]">{r.transfer_number}</td>
                                        <td className="px-3 py-1 text-slate-600">{r.from_site}</td>
                                        <td className="px-3 py-1 text-slate-600">{r.to_site}</td>
                                        <td className="px-3 py-1 truncate max-w-[260px]" title={r.product_name}>{r.product_name}</td>
                                        <td className="px-3 py-1 text-right tabular-nums">{r.qty}</td>
                                        <td className="px-3 py-1 text-slate-500">{r.unit}</td>
                                        <td className="px-3 py-1 text-right tabular-nums text-slate-500">{r.price_per_unit ? formatCurrency(r.price_per_unit) : '-'}</td>
                                        <td className="px-3 py-1 text-right tabular-nums font-semibold">{r.total ? formatCurrency(r.total) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Section 3: source invoices (LIFO allocation) */}
            {detail.source_invoices && detail.source_invoices.length > 0 && (
                <Card data-testid="refac-source-invoices-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <Receipt className="w-3.5 h-3.5 text-slate-500" />
                            3. Factures d'origine
                            <span className="text-[10px] font-normal text-slate-400 ml-1">
                                (documents source du stock refacturé, allocation LIFO par transfert)
                            </span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 border-b text-[10px] uppercase text-slate-500">
                                <tr>
                                    <th className="text-left px-3 py-1.5 w-[220px]">Fournisseur</th>
                                    <th className="text-left px-3 py-1.5">N° de facture</th>
                                    <th className="text-left px-3 py-1.5 w-[110px]">Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.source_invoices.flatMap((group, gi) =>
                                    group.invoices.map((inv, ii) => {
                                        const noDoc = inv.document_number === '(sans n°)';
                                        return (
                                            <tr
                                                key={`${gi}-${ii}`}
                                                className={`border-b border-slate-100 ${noDoc ? 'bg-amber-50' : (ii % 2 === 1 ? 'bg-slate-50' : 'bg-white')}`}
                                                data-testid={`refac-source-invoice-row-${gi}-${ii}`}
                                            >
                                                <td className="px-3 py-1 text-slate-700 font-semibold">
                                                    {ii === 0 ? group.supplier : ''}
                                                </td>
                                                <td className={`px-3 py-1 font-mono text-[11px] ${noDoc ? 'text-amber-800 italic' : ''}`}>
                                                    {inv.document_number}
                                                </td>
                                                <td className="px-3 py-1 text-slate-600 tabular-nums">
                                                    {inv.date ? (() => {
                                                        const [y, m, d] = inv.date.split('-');
                                                        return `${d}/${m}/${y}`;
                                                    })() : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                        {detail.source_invoices.some(g => g.invoices.some(inv => inv.document_number === '(sans n°)')) && (
                            <div className="px-3 py-2 text-[10px] text-amber-800 bg-amber-50 border-t border-amber-200 italic">
                                Les lignes en jaune correspondent à des réceptions sans n° de facture saisi.
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}
        </>
    );
}
